import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDocumentWriter } from "@/contexts/DocumentWriterContext";
import { DocumentCanvas } from "@/components/document-writer/DocumentCanvas";
import { DocumentWriterSidebar } from "@/components/document-writer/DocumentWriterSidebar";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import type { UIMessage } from "@convex-dev/agent/react";
import {
  FileText,
  FileSignature,
  Receipt,
  FileCheck,
  DollarSign,
  Sparkles,
  Send,
  Loader2,
  ClipboardList,
  Mail,
  Files,
  ArrowLeft,
  Palette,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Template type definition
type TemplateType =
  | "custom"
  | "proposal"
  | "invoice"
  | "service-agreement"
  | "quote"
  | "scope-of-work"
  | "letter";

export default function DocumentWriter() {
  // ===== STATE MANAGEMENT =====
  const [prompt, setPrompt] = useState("");

  // ===== CONTEXT & HOOKS =====
  const location = useLocation(); // Moved up to be available for initial state calculation
  const navigate = useNavigate();
  const { user } = useUser();
  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  const isFreePlan = subscription !== undefined && (!subscription?.amount || subscription.amount === 0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Initialize viewState based on navigation state - if generating from prompt, start in generating state
  const [viewState, setViewState] = useState<"input" | "generating" | "complete">(() => {
    const state = location.state as { generateFromPrompt?: boolean } | null;
    return state?.generateFromPrompt ? "generating" : "input";
  });

  const [threadId, setThreadId] = useState<string | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null); // Track workflow ID for cancellation
  // State for alert
  const [showCreditAlert, setShowCreditAlert] = useState(false);

  // Fetch usage data
  const usage = useQuery(api.usage.getUsage);
  const [isStarting, setIsStarting] = useState(false); // Track if generation is starting
  const [hasInitialized, setHasInitialized] = useState(false); // Track if component has initialized
  const [isPdfReady, setIsPdfReady] = useState(false); // Track if PDF compilation is complete
  const [useBrandColors, setUseBrandColors] = useState(false); // Default brand colors toggle OFF

  // Fetch brand colors for the organization
  const brandColors = useQuery(api.organizations.getBrandColors);

  const {
    showSidebar,
    hideSidebar,
    threadId: contextThreadId,
    setThreadId: setContextThreadId,
    clearThread,
    setActiveDocument,
    registerScrollCallback,
    unregisterScrollCallback,
  } = useDocumentWriter();
  const { toast } = useToast();

  // Check if we're loading an existing conversation from chat history
  const navigationState = location.state as {
    loadThreadId?: string;
    hasDocument?: boolean;
    fromChatHistory?: boolean;
    generateFromPrompt?: boolean;
    prompt?: string;
    templateType?: TemplateType;
    relatedBidId?: string;
    relatedClientId?: string;
    fromDocuments?: boolean;
    // Template-based generation fields
    generateFromTemplate?: boolean;
    templateId?: string;
    templateName?: string;
    userInstructions?: string;
  } | null;

  // ===== CONVEX MUTATIONS =====
  const createThread = useMutation(api.documentWriterStreaming.createDocumentThread);
  // Phase 1: Use workflow-based generation with automatic validation
  const initiateGeneration = useMutation(
    api.documentWriterStreaming.initiateDocumentGenerationWorkflow
  );

  // ===== CONVEX QUERIES =====
  // ✅ STREAMING PATTERN: Use useQuery with streamArgs for real-time updates
  // This matches the pattern used in RefactoredAgentChatSidebar
  const messagesResponse = useQuery(
    api.documentWriterStreaming.listDocumentMessages,
    threadId
      ? {
          threadId,
          paginationOpts: { cursor: null, numItems: 100 },
          streamArgs: { kind: "list" as const }, // Enable streaming via streamArgs
        }
      : "skip"
  );

  // Fetch the document associated with this thread (if any)
  const threadDocument = useQuery(
    api.documents.getThreadDocument,
    threadId ? { threadId } : "skip"
  );

  // DEBUG: Log threadDocument state
  useEffect(() => {
    console.log("[DocumentWriter] threadDocument state:", {
      threadId,
      hasThreadDocument: !!threadDocument,
      documentId: threadDocument?._id,
      hasTypstContent: !!threadDocument?.typstContent,
      typstContentLength: threadDocument?.typstContent?.length,
      outputType: threadDocument?.outputType,
    });
  }, [threadDocument, threadId]);

  // Extract UIMessages from the response
  const uiMessages = useMemo(() => {
    if (!messagesResponse) return undefined;
    return messagesResponse.page as UIMessage[];
  }, [messagesResponse]);

  // Extract the latest assistant message for status tracking
  // NOTE: The document content is NOT in the message - it's saved to the documents table
  // The agent sends a short conversational message (e.g., "I've generated your proposal...")
  // So we look for ANY assistant message, not just long ones
  const latestAssistantMessage = uiMessages?.filter((msg) => msg.role === "assistant").pop();

  // DEBUG: Log message state
  useEffect(() => {
    console.log("[DocumentWriter] Message state:", {
      threadId,
      hasMessages: !!uiMessages,
      messageCount: uiMessages?.length,
      hasLatestAssistantMessage: !!latestAssistantMessage,
      latestMessageStatus: latestAssistantMessage?.status,
      latestMessageText: latestAssistantMessage?.text?.substring(0, 100),
    });
  }, [uiMessages, latestAssistantMessage, threadId]);

  const hasStartedAutoGeneration = useRef(false); // Track if auto-generation has been triggered
  const canvasContainerRef = useRef<HTMLDivElement>(null); // Ref for the canvas container
  // Track when we start waiting for a document after a 'success' message with no content yet.
  // If the document doesn't appear within 60 s we escape the generating loop.
  const successNoDocumentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Register scroll callback for the sidebar to trigger scroll to top on refinement
  useEffect(() => {
    const scrollToTop = () => {
      if (canvasContainerRef.current) {
        // Scroll the canvas container to top smoothly
        canvasContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        // Also scroll the window to top in case the container isn't the scrolled element
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    registerScrollCallback(scrollToTop);

    return () => {
      unregisterScrollCallback();
    };
  }, [registerScrollCallback, unregisterScrollCallback]);

  // ===== EFFECTS =====
  // Initialize component on mount
  useEffect(() => {
    console.log("[DocumentWriter] Page mounted");

    // PRIORITY 1: Check if we're generating a new document from proposal settings or similar
    if (navigationState?.generateFromPrompt) {
      console.log("[DocumentWriter] Generating new document from navigation state");
      // Clear any persisted thread to start fresh
      clearThread();
      setThreadId(null);
      // NOTE: Do NOT set viewState to "input" here, as it causes a flash.
      // We initialized viewState to "generating" in useState if generateFromPrompt is true.
      showSidebar();
    }
    // PRIORITY 2: Check if we're loading an existing conversation from chat history
    else if (navigationState?.fromChatHistory && navigationState?.loadThreadId) {
      console.log(
        "[DocumentWriter] Loading existing conversation from chat history:",
        navigationState.loadThreadId
      );
      setThreadId(navigationState.loadThreadId);
      setContextThreadId(navigationState.loadThreadId); // Sync with context
      setViewState("generating"); // Show the document canvas
      showSidebar(); // Keep sidebar visible
    }
    // PRIORITY 2.5: Check if we're creating a new document (e.g., from Documents page)
    else if (navigationState?.fromDocuments) {
      console.log("[DocumentWriter] Creating new document (from Documents page)");
      clearThread();
      setThreadId(null);
      setViewState("input");
      showSidebar();
    }
    // PRIORITY 3: Load persisted thread from context (if any)
    else if (contextThreadId) {
      console.log("[DocumentWriter] Loading persisted thread from context:", contextThreadId);
      setThreadId(contextThreadId);
      setViewState("generating"); // Show the document canvas
      showSidebar();
    }
    // PRIORITY 4: Start fresh
    else {
      console.log("[DocumentWriter] Starting fresh - no persisted thread");
      showSidebar(); // Auto-open sidebar on page load
    }

    setHasInitialized(true); // Mark as initialized
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      // Clean up the escape timer if the component unmounts mid-generation
      if (successNoDocumentTimerRef.current) {
        clearTimeout(successNoDocumentTimerRef.current);
      }
    };
  }, []); // Removed isFreePlan dependency

  // Handle navigation state changes (e.g., when clicking history items while already on the page)
  useEffect(() => {
    // Only run after initialization and when we have navigation state from chat history
    if (!hasInitialized || !navigationState?.fromChatHistory || !navigationState?.loadThreadId) {
      return;
    }

    console.log(
      "[DocumentWriter] Navigation state changed, loading thread:",
      navigationState.loadThreadId
    );

    // Load the thread from navigation state
    setThreadId(navigationState.loadThreadId);
    setContextThreadId(navigationState.loadThreadId); // Sync with context
    setViewState("generating"); // Show the document canvas

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState?.loadThreadId, navigationState?.fromChatHistory, hasInitialized]);

  // Associate document with thread when document is loaded
  // Track the last document ID we set to avoid infinite loops
  const lastSetDocumentIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (threadDocument && threadId && threadDocument._id !== lastSetDocumentIdRef.current) {
      console.log(
        "[DocumentWriter] Associating document with thread:",
        threadDocument._id,
        "threadId:",
        threadId
      );
      lastSetDocumentIdRef.current = threadDocument._id;
      setActiveDocument(threadDocument._id);
    }
  }, [threadDocument, threadId, setActiveDocument]);

  // Safety check: Detect if the thread has been deleted while viewing it
  // This handles edge cases where the thread is deleted from another tab or by another user
  useEffect(() => {
    // Only check if we have a threadId and are in generating/complete state
    if (!threadId || viewState === "input") return;

    // If uiMessages is defined (query completed) but has no messages,
    // and we're not in the initial loading state, the thread was likely deleted
    if (uiMessages !== undefined && uiMessages.length === 0 && !isStarting) {
      console.log(
        "[DocumentWriter] Thread appears to have been deleted, resetting to initial state"
      );

      // Reset to initial state
      setThreadId(null);
      setViewState("input");
      clearThread();

      toast({
        title: "Conversation no longer available",
        description: "The conversation was deleted. Starting fresh.",
        variant: "default",
        duration: 5000,
      });
    }
  }, [threadId, viewState, uiMessages, isStarting, clearThread, toast]);

  // Sync state when context thread is cleared (e.g. from Sidebar "New Document" button)
  useEffect(() => {
    // If we have a local threadId but the context threadId is null,
    // it means the thread was cleared from the sidebar (New Document clicked)
    if (threadId && contextThreadId === null) {
      console.log("[DocumentWriter] Context thread cleared, resetting to input state");
      setThreadId(null);
      setViewState("input");
      setIsPdfReady(false);
      setWorkflowId(null);
      setPrompt("");
    }
  }, [threadId, contextThreadId]);

  // Update view state based on document completion
  // The document is complete when:
  // 1. We have a threadDocument with content (document was saved by the agent)
  // 2. OR we have an assistant message with "success" status
  useEffect(() => {
    // Check if document has been saved to the database
    const hasDocumentContent =
      threadDocument &&
      (threadDocument.typstContent || threadDocument.content || threadDocument.latexContent);

    // Check if we have any assistant message (even short ones)
    const hasAssistantMessage = latestAssistantMessage !== undefined;

    console.log("[DocumentWriter] State update check:", {
      hasAssistantMessage,
      messageStatus: latestAssistantMessage?.status,
      messageTextLength: latestAssistantMessage?.text?.length,
      hasDocumentContent,
      documentOutputType: threadDocument?.outputType,
      isPdfReady,
      currentViewState: viewState,
    });

    // Reset loading state once we have a message
    if (hasAssistantMessage) {
      setIsStarting(false);
    }

    // CRITICAL: Only transition to "complete" when ALL conditions are met:
    // 1. Document has been saved to the database (hasDocumentContent)
    // 2. PDF compilation is complete (isPdfReady) OR it's not a Typst document
    // 3. Document status is "completed" (or finalized) and is NOT actively retrying validation/visual review
    const isTypstDocument = threadDocument?.outputType === "typst";
    const hasServerPdf = !!threadDocument?.compiledPdfStorageId;
    const isDocumentDone =
      threadDocument?.status === "completed" ||
      threadDocument?.status === "finalized_ready_for_signature" ||
      (hasServerPdf && !threadDocument?.isRetrying);
    const isRetrying = threadDocument?.isRetrying === true;
    const isDocumentFullyReady =
      hasDocumentContent && (isPdfReady || hasServerPdf || !isTypstDocument) && isDocumentDone && !isRetrying;

    if (isDocumentFullyReady) {
      // Document arrived and completed — clear any pending escape timer
      if (successNoDocumentTimerRef.current) {
        clearTimeout(successNoDocumentTimerRef.current);
        successNoDocumentTimerRef.current = null;
      }
      console.log(
        "[DocumentWriter] Document fully ready (saved + PDF compiled + status completed), transitioning to complete state"
      );
      setViewState("complete");
      return;
    }

    // If document is saved but PDF is still compiling, stay in generating state
    if (hasDocumentContent && !isPdfReady && !hasServerPdf && isTypstDocument) {
      console.log(
        "[DocumentWriter] Document saved but PDF still compiling, staying in generating state"
      );
      setViewState("generating");
      return;
    }

    // Otherwise, check message status for streaming state
    if (latestAssistantMessage) {
      if (latestAssistantMessage.status === "streaming") {
        console.log("[DocumentWriter] Message is streaming");
        setViewState("generating");
      } else if (latestAssistantMessage.status === "success") {
        // Message is complete, but document might still be saving.
        // Start a 60-second escape timer so we never get permanently stuck.
        if (!successNoDocumentTimerRef.current) {
          console.log(
            "[DocumentWriter] Message complete, waiting for document to be saved (60s timeout)..."
          );
          successNoDocumentTimerRef.current = setTimeout(() => {
            console.warn(
              "[DocumentWriter] 60s timeout reached — no document appeared after success message. Escaping generating loop."
            );
            successNoDocumentTimerRef.current = null;
            setViewState("complete");
            toast({
              title: "Document generation timed out",
              description: "The document took too long to appear. Please try again.",
              variant: "destructive",
            });
          }, 60_000);
        }
        setViewState("generating");
      } else if (latestAssistantMessage.status === "failed") {
        // Clear any pending timer
        if (successNoDocumentTimerRef.current) {
          clearTimeout(successNoDocumentTimerRef.current);
          successNoDocumentTimerRef.current = null;
        }
        console.log("[DocumentWriter] Message failed");
        setViewState("complete"); // Show the canvas with error state
        toast({
          title: "Document generation failed",
          description: "There was an error generating your document. Please try again.",
          variant: "destructive",
        });
      }
    }
  }, [latestAssistantMessage, threadDocument, toast, viewState, isPdfReady]);

  // ===== HANDLERS =====

  // Handle PDF ready state changes from DocumentCanvas
  const handlePdfReady = useCallback((isReady: boolean) => {
    setIsPdfReady((prev) => {
      if (prev !== isReady) {
        console.log("[DocumentWriter] PDF ready state changed:", isReady);
        return isReady;
      }
      return prev;
    });
  }, []);

  const handleGenerate = useCallback(
    async (
      overridePrompt?: string,
      overrideTemplateType?: TemplateType,
      additionalContext?: string,
      isTemplateBasedGeneration?: boolean
    ) => {
      const purchasedCredits = (usage?.purchasedCredits ?? (subscription as any)?.purchasedCredits ?? 0);
      const hasPurchasedCredits = purchasedCredits > 0;
      const monthlyRemaining = usage ? Math.max(0, usage.creditLimit - usage.creditsUsed) : 0;
      const hasActivePaidPlan = Boolean(subscription && subscription.status === "active" && (subscription.amount || 0) > 0);

      // Only block if user has no top-up credits AND no remaining monthly plan credits
      if (!hasPurchasedCredits && (!hasActivePaidPlan || monthlyRemaining <= 0)) {
        setShowUpgradeModal(true);
        return;
      }

      const promptToUse = overridePrompt || prompt;
      const templateTypeToUse = overrideTemplateType || "custom";

      console.log("🔵 [handleGenerate] CALLED", {
        overridePrompt,
        overrideTemplateType,
        additionalContext,
        promptToUse,
        promptTrimmed: promptToUse.trim(),
        promptLength: promptToUse.trim().length,
        isStarting,
        templateTypeToUse,
      });

      if (!promptToUse.trim() || isStarting) {
        console.log("🔴 [handleGenerate] EARLY RETURN", {
          reason: !promptToUse.trim() ? "Empty prompt" : "Already starting",
          promptToUse,
          isStarting,
        });
        return;
      }

      console.log("✅ [handleGenerate] Validation passed, starting generation...");
      setIsStarting(true); // Show loading state on button

      try {
        const enhancedPrompt = promptToUse.trim();

        console.log("📝 [handleGenerate] Creating thread...");
        // STEP 1: Create a new thread for this document
        // We pass the CLEAN prompt to createThread so the chat history looks nice
        const { threadId: newThreadId } = await createThread({
          prompt: enhancedPrompt,
          documentType: templateTypeToUse, // Pass template type for thread categorization
        });

        console.log("✅ [DocumentWriter] Created new thread:", newThreadId);
        console.log("   Template type:", templateTypeToUse);
        console.log("   Enhanced prompt:", enhancedPrompt);
        if (additionalContext) {
          console.log("   Additional context:", additionalContext);
        }

        // STEP 2: Set thread ID in both local state and context IMMEDIATELY
        // This ensures the thread persists even if component remounts
        setThreadId(newThreadId);
        setContextThreadId(newThreadId); // This persists to localStorage

        // STEP 3: Update UI state to show generating state
        // This must happen BEFORE starting workflow so DocumentCanvas shows loading
        setViewState("generating");
        showSidebar(); // Show sidebar for refinement requests

        console.log("✅ [DocumentWriter] UI state set to generating, about to start workflow");

        // STEP 4: Start document generation workflow (Phase 1)
        // We pass the additionalContext here so it gets saved as a hidden system message
        // Include explicit brand colors instruction based on the user toggle
        let finalContext = additionalContext || "";
        if (useBrandColors && brandColors?.primaryColor) {
          const brandColorContext = `BRAND_COLORS: ENABLED. The user explicitly chose to apply their organization brand colors. Use these exact colors for accents, key headings, highlight lines, banner accents, and table headers - Primary: ${brandColors.primaryColor}, Secondary: ${brandColors.secondaryColor || brandColors.primaryColor}.`;
          finalContext = finalContext ? `${finalContext}\n${brandColorContext}` : brandColorContext;
        } else {
          const brandColorContext = `BRAND_COLORS: DISABLED. Do NOT force the organization's saved brand colors. Instead, design with a rich, polished, visually stunning professional color palette appropriate for the document (such as executive deep navy #0f2b5c / #1e3a8a, vibrant royal blue #2563eb / #1d4ed8, refined corporate indigo, or sophisticated emerald accents). Ensure the document has beautiful, vibrant, professional colors for headers, banner backgrounds, table headers, and accents—do NOT make everything black, gray, or monochrome.`;
          finalContext = finalContext ? `${finalContext}\n${brandColorContext}` : brandColorContext;
        }

        const { workflowId: newWorkflowId } = await initiateGeneration({
          threadId: newThreadId,
          prompt: enhancedPrompt,
          additionalContext: finalContext || undefined,
          isTemplateBasedGeneration: isTemplateBasedGeneration || false,
        });

        // Store workflow ID for cancellation
        setWorkflowId(newWorkflowId);

        console.log("✅ [DocumentWriter] Document generation initiated successfully");
        console.log("   Workflow ID:", newWorkflowId);
      } catch (error: any) {
        console.error("❌ [handleGenerate] Failed to generate document:", error);

        // Check if this is a usage limit exceeded error
        const errorMessage = error?.data?.message || error?.message || "";
        const errorCode = error?.data?.code || "";

        console.error("❌ [handleGenerate] Detailed error:", { errorMessage, errorCode, error });

        // If the thread is missing or synchronization failed, clear it to force a fresh start
        if (
          errorMessage.includes("Thread not found") ||
          errorMessage.includes("Parent message not found") ||
          errorMessage.includes("synchronization error") ||
          errorMessage.includes("Invalid document thread")
        ) {
          console.warn(
            "⚠️ [handleGenerate] Stale thread detected. Clearing threadId and suggesting retry."
          );
          clearThread();
          setThreadId(null);
          toast({
            title: "Session issue detected",
            description:
              "Your document session had an issue. We've reset it — please try clicking generate again.",
            variant: "destructive",
          });
          setIsStarting(false);
          return;
        }

        if (
          errorCode === "USAGE_LIMIT_EXCEEDED" ||
          errorMessage.includes("credit limit") ||
          errorMessage.includes("exceeded")
        ) {
          setShowUpgradeModal(true);
          setIsStarting(false);
          return;
        }

        // Provide specific error messages based on error type
        let displayMessage = "Failed to start document generation";

        if (error instanceof Error) {
          if (error.message.includes("Not authenticated")) {
            displayMessage = "You must be logged in to generate documents";
          } else if (error.message.includes("network") || error.message.includes("fetch")) {
            displayMessage = "Network error. Please check your connection and try again.";
          } else {
            displayMessage = error.message;
          }
        }

        toast({
          title: "Generation failed",
          description: displayMessage,
          variant: "destructive",
        });

        // Reset loading state on error
        setIsStarting(false);
      }
    },
    [
      prompt,
      isStarting,
      createThread,
      initiateGeneration,
      setContextThreadId,
      showSidebar,
      toast,
      usage,
      useBrandColors,
      brandColors,
    ]
  );

  // Handle auto-generation from navigation state
  useEffect(() => {
    if (!hasInitialized) return;

    // Run if we have a prompt and haven't started yet
    // We allow running if viewState is "input" OR "generating" (since we now initialize to generating)
    if (
      navigationState?.generateFromPrompt &&
      navigationState?.prompt &&
      !isStarting &&
      !hasStartedAutoGeneration.current
    ) {
      console.log("[DocumentWriter] Auto-generating from navigation state");

      // Mark as started to prevent loops
      hasStartedAutoGeneration.current = true;

      // Set the display prompt (without IDs)
      setPrompt(navigationState.prompt);

      // Build context string with IDs (will be passed as hidden system message)
      // The agent instructions look for BID_ID: in the context
      let contextWithIds = "";
      if (navigationState.relatedBidId) {
        contextWithIds += `BID_ID: ${navigationState.relatedBidId}\n`;
      }
      if (navigationState.relatedClientId) {
        contextWithIds += `CLIENT_ID: ${navigationState.relatedClientId}\n`;
      }

      // Use the clean prompt for display and chat history
      // Pass IDs via additionalContext (hidden from chat UI)
      const startGeneration = async () => {
        const isSystemTemplate =
          navigationState.templateType === "proposal" ||
          navigationState.templateType === "service-agreement";
        await handleGenerate(
          navigationState.prompt,
          navigationState.templateType,
          contextWithIds || undefined,
          isSystemTemplate
        );

        // CRITICAL: Clear the generateFromPrompt flag to prevent infinite loops
        // We replace the current history entry so the user can't "back" into a re-generation loop
        navigate(location.pathname, {
          replace: true,
          state: {
            ...navigationState,
            generateFromPrompt: false,
          },
        });
      };

      startGeneration();
    }
  }, [
    hasInitialized,
    navigationState,
    isStarting,
    viewState,
    handleGenerate,
    navigate,
    location.pathname,
  ]);

  // Handle template-based generation from navigation state
  useEffect(() => {
    if (!hasInitialized) return;

    // Run if we're generating from a template and haven't started yet
    if (
      navigationState?.generateFromTemplate &&
      navigationState?.templateId &&
      !isStarting &&
      !hasStartedAutoGeneration.current
    ) {
      console.log("[DocumentWriter] Auto-generating from template:", navigationState.templateId);

      // Mark as started to prevent loops
      hasStartedAutoGeneration.current = true;

      // Set the display prompt from user instructions
      const templatePrompt =
        navigationState.userInstructions ||
        `Create a document using template: ${navigationState.templateName || "Unknown"}`;
      setPrompt(templatePrompt);

      // Build the additional context for the agent with template ID
      const contextString = `USE_TEMPLATE_ID: ${navigationState.templateId}\nTEMPLATE_NAME: ${navigationState.templateName || "User Template"}\n`;

      // Use the user instructions as prompt, pass template ID as context
      // Pass true for isTemplateBasedGeneration to get reduced credit cost (5 vs 15)
      const startGeneration = async () => {
        await handleGenerate(templatePrompt, "custom", contextString, true);

        // Clear the generateFromTemplate flag to prevent infinite loops
        navigate(location.pathname, {
          replace: true,
          state: {
            ...navigationState,
            generateFromTemplate: false,
          },
        });
      };

      startGeneration();
    }
  }, [hasInitialized, navigationState, isStarting, handleGenerate, navigate, location.pathname]);

  const handleRetry = async () => {
    if (!prompt.trim()) return;

    // Reset state and try again
    setThreadId(null);
    setViewState("input");
    setIsPdfReady(false); // Reset PDF ready state

    // Wait a moment for UI to update
    setTimeout(() => {
      handleGenerate();
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  const handleNewDocument = () => {
    setViewState("input");
    setIsPdfReady(false); // Reset PDF ready state for new document
    setThreadId(null);
    setWorkflowId(null); // Clear workflow ID
    clearThread(); // Clear the sidebar context thread
    setPrompt("");
    showSidebar(); // Keep sidebar open to show history view
  };

  // ===== RENDER =====
  const { isSidebarVisible } = useDocumentWriter();

  return (
    <>
      <UpgradePromptDialog
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to Starter"
        description="Use the AI Document Writer to automatically generate, format, and review important business documents."
        feature="AI Document Writer"
        planName="Starter"
        planFeatures={["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]}
      />
      <DashboardLayout
        activeTab="document-writer"
        showAgentSidebar={false} // Use custom DocumentWriterSidebar instead
      >
        <div
          className={`min-h-[calc(100vh-8rem)] px-4 py-8 transition-all duration-500 ease-in-out ${
            isSidebarVisible ? "lg:mr-96" : ""
          }`}
        >
          {/* INITIAL STATE: Message Input View */}
          {viewState === "input" && (
            <div className="flex min-h-[calc(100vh-12rem)] flex-col items-center justify-center">
              <div className="mx-auto w-full max-w-4xl space-y-8">
                {/* Greeting */}
                <div className="text-center">
                  <h1 className="mb-2 text-4xl font-normal text-purple-600 md:text-5xl">Hello,</h1>
                  <p className="text-lg text-gray-600">
                    What document would you like to create today?
                  </p>
                </div>

                {/* Input Card */}
                <Card className="border-gray-200 p-6 shadow-lg">
                  <div className="space-y-4">
                    {/* Textarea Input */}
                    <div className="relative">
                      <Textarea
                        placeholder="Describe the document you want to create using your business data..."
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="min-h-[120px] resize-none border-gray-300 text-base focus:border-purple-500 focus:ring-purple-500"
                      />
                    </div>

                    {/* Action Buttons Row */}
                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 text-gray-600"
                          onClick={() => navigate("/documents")}
                        >
                          <Files className="h-4 w-4" />
                          My Documents
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-2 text-gray-600"
                          onClick={() => navigate("/templates")}
                        >
                          <FileText className="h-4 w-4" />
                          My Templates
                        </Button>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* Brand Colors Toggle */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                <Switch
                                  id="brand-colors"
                                  checked={useBrandColors}
                                  onCheckedChange={setUseBrandColors}
                                  disabled={!brandColors?.primaryColor}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                                <Label
                                  htmlFor="brand-colors"
                                  className={cn(
                                    "flex cursor-pointer items-center gap-1.5 text-sm",
                                    !brandColors?.primaryColor
                                      ? "text-gray-400"
                                      : useBrandColors
                                        ? "text-purple-600"
                                        : "text-gray-600"
                                  )}
                                >
                                  <Palette className="h-4 w-4" />
                                  Brand Colors
                                </Label>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              {brandColors?.primaryColor ? (
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-3 w-3 rounded-full border"
                                    style={{ backgroundColor: brandColors.primaryColor }}
                                  />
                                  <div
                                    className="h-3 w-3 rounded-full border"
                                    style={{
                                      backgroundColor:
                                        brandColors.secondaryColor || brandColors.primaryColor,
                                    }}
                                  />
                                  <span>Apply brand colors to document</span>
                                </div>
                              ) : (
                                <span>Set up brand colors in Settings → Brand Colors</span>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <Button
                          onClick={() => {
                            console.log("🟢 [Generate Button] CLICKED", { prompt, isStarting });
                            handleGenerate();
                          }}
                          disabled={!prompt.trim() || isStarting}
                          className="gap-2 bg-purple-600 text-white hover:bg-purple-700"
                        >
                          {isStarting ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Starting...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              Generate
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Help Text */}
                <div className="mx-auto max-w-2xl text-center text-sm text-gray-500">
                  <p>
                    Describe the document you need, and our AI will help you create it. You have
                    full creative control over the layout and content.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* GENERATING/COMPLETE STATE: Document Canvas View */}
          {(viewState === "generating" || viewState === "complete") && (
            <div ref={canvasContainerRef} className="mx-auto w-full max-w-6xl space-y-4">
              {/* Header with New Document button */}
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    {navigationState?.fromDocuments && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 pl-0"
                        onClick={() => navigate("/documents")}
                      >
                        <ArrowLeft className="h-4 w-4" /> Back to Documents
                      </Button>
                    )}
                    {navigationState?.relatedBidId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 pl-0"
                        onClick={() => navigate(`/bids/${navigationState.relatedBidId}`)}
                      >
                        <ArrowLeft className="h-4 w-4" /> Back to Bid
                      </Button>
                    )}
                  </div>
                  <h1 className="text-2xl font-semibold text-gray-900">Document Writer</h1>
                </div>
                <Button onClick={handleNewDocument} variant="outline" className="gap-2">
                  <FileText className="h-4 w-4" />
                  New Document
                </Button>
              </div>

              {/* Document Canvas */}
              <DocumentCanvas
                message={latestAssistantMessage}
                threadDocument={threadDocument}
                onPdfReady={handlePdfReady}
                workflowId={workflowId || undefined}
                threadId={threadId || undefined}
                onRefineRequest={() => showSidebar()}
                onRetryRequest={handleRetry}
              />
            </div>
          )}
        </div>
      </DashboardLayout>

      {/* Document Writer Sidebar - Fixed position on desktop */}
      {isSidebarVisible && (
        <div className="fixed right-0 top-16 z-40 hidden h-[calc(100vh-4rem)] w-96 shadow-lg lg:block">
          <DocumentWriterSidebar fillParent={true} hasActiveDocument={!!threadId} />
        </div>
      )}


    </>
  );
}
